import { faBriefcase } from '@fortawesome/pro-light-svg-icons/faBriefcase'
import { faCog } from '@fortawesome/pro-light-svg-icons/faCog'
import { faComment } from '@fortawesome/pro-light-svg-icons/faComment'
import { faDisplayCode } from '@fortawesome/pro-light-svg-icons/faDisplayCode'
import { faGamepad } from '@fortawesome/pro-light-svg-icons/faGamepad'
import { faGraduationCap } from '@fortawesome/pro-light-svg-icons/faGraduationCap'
import { faHandshake } from '@fortawesome/pro-light-svg-icons/faHandshake'
import { faHexagonVerticalNft } from '@fortawesome/pro-light-svg-icons/faHexagonVerticalNft'
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus'
import { faSparkles } from '@fortawesome/pro-light-svg-icons/faSparkles'
import { faUser } from '@fortawesome/pro-light-svg-icons/faUser'
import { faUserGear } from '@fortawesome/pro-light-svg-icons/faUserGear'
import { faUserTie } from '@fortawesome/pro-light-svg-icons/faUserTie'
import { faUserVneck } from '@fortawesome/pro-light-svg-icons/faUserVneck'
import { faWallet } from '@fortawesome/pro-light-svg-icons/faWallet'
import { Schema, object, string } from 'yup'

import { TierOfferings } from '@infura/packages.api'
import { ServiceTier } from '@infura/packages.uks'

import { ISurveyQuestionData } from 'components/onboardingWelcome'

import { onboardingCategoryValidator, onboardingRoleValidator, onboardingUsageValidator } from 'lib/forms/validators'

/**
 * Define all your inputs validation as a schema
 * For a wizzard Form define an array of Schema
 * See: https://github.com/jquense/yup
 */
export const getOnboardingSurveyQuestionsSchema = (questions: Array<ISurveyQuestionData>): Array<Schema> => {
  const validationSchema = []

  questions.forEach(question => {
    if (question.id === 'welcome_to_infura') {
      validationSchema.push(
        // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
        object().shape({
          ...onboardingRoleValidator,
          ...onboardingUsageValidator
        })
      )
    }

    if (question.id === 'select_your_category') {
      validationSchema.push(
        // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
        object().shape({
          ...onboardingCategoryValidator
        })
      )
    }

    if (question.id === 'plan_selection') {
      validationSchema.push(
        // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
        object().shape({
          tier: string().required('Please select a plan')
        })
      )
    }
  })

  return validationSchema
}

export const getOnboardingSurveyQuestions = (
  tiers: TierOfferings,
  isOnCreditsPlan: boolean,
  isGrowthPlanDisabled: boolean
): Array<ISurveyQuestionData> => {
  return [
    {
      component: 'Question',
      datas: [
        {
          description: 'Tell us your role so we can show you the features and contents that matter to you',
          id: 'role',
          options: [
            {
              icon: faDisplayCode,
              id: 'developer',
              label: 'Engineer',
              subtitle: ''
            },
            {
              icon: faUserVneck,
              id: 'productmanager',
              label: 'Product Manager',
              subtitle: ''
            },
            {
              icon: faUserGear,
              id: 'sysadmin',
              label: 'System Administrator',
              subtitle: ''
            },
            {
              icon: faUserTie,
              id: 'founder',
              label: 'Founder',
              subtitle: ''
            },
            {
              icon: faPlus,
              id: 'other',
              label: 'Other',
              subtitle: '',
              textarea: { placeholder: 'Your role' }
            }
          ],
          required: true,
          title: 'Select your role',
          tooltip:
            "We're asking for your role so we can tailor our product to your needs. Your role information helps us understand how you will use our product and what features you may find most helpful. Rest assured that we will keep this information confidential and use it only to provide you with the best experience possible.",
          type: 'radio'
        },
        {
          id: 'usage',
          options: [
            {
              icon: faUser,
              id: 'me',
              label: 'Just Me'
            },
            {
              icon: faBriefcase,
              id: 'team',
              label: 'My Organization / Team'
            }
          ],
          required: true,
          title: 'Who’s building?',
          type: 'radio'
        }
      ],
      id: 'welcome_to_infura',
      nextCTA: 'Next',
      sideBarData: {
        title: 'About you'
      }
    },
    {
      component: 'Question',
      datas: [
        {
          columns: 'threeColumn',
          description:
            'Choose a category below that best fits the type of application or integration you want to build with us.',
          id: 'category',
          options: [
            {
              icon: faSparkles,
              id: 'defi',
              label: 'DeFi',
              subtitle: ''
            },
            {
              icon: faWallet,
              id: 'wallet',
              label: 'Wallet',
              subtitle: ''
            },
            {
              icon: faComment,
              id: 'social',
              label: 'Social',
              subtitle: ''
            },
            {
              icon: faHandshake,
              id: 'advisory',
              label: 'Advisory',
              subtitle: ''
            },
            {
              icon: faCog,
              id: 'infrastructure',
              label: 'Infrastructure',
              subtitle: ''
            },
            {
              icon: faHexagonVerticalNft,
              id: 'nfts',
              label: 'NFTs',
              subtitle: ''
            },
            {
              icon: faGamepad,
              id: 'gaming',
              label: 'Gaming',
              subtitle: ''
            },
            {
              icon: faGraduationCap,
              id: 'learn',
              label: "I'm here to learn"
            },
            {
              icon: faPlus,
              id: 'other',
              label: 'Other',
              textarea: { placeholder: 'Marketplace' }
            }
          ],
          required: true,
          title: 'What are you building?',
          tooltip:
            "We're asking you to select an application category so we can provide you with the most relevant resources and guidance for building your application. This information helps us understand what features you may need and how you plan to use our product so we can provide you with the best experience possible.",
          type: 'radio'
        }
      ],
      id: 'select_your_category',
      nextCTA: 'Next',
      sideBarData: {
        title: 'App category'
      }
    },
    {
      component: 'PlanTier',
      datas: [
        {
          columns: 'oneColumn',
          id: 'tier',
          options: [
            {
              highlight: 'New to Web3',
              id: ServiceTier.CORE,
              infoList: isOnCreditsPlan ? tiers?.core?.creditPlanSellingPoints : tiers?.core?.sellingPoints,
              label: 'Core',
              subtitle: 'Free'
            },
            {
              highlight: 'Most Popular',
              id: ServiceTier.DEVELOPER,
              infoList: isOnCreditsPlan ? tiers?.developer?.creditPlanSellingPoints : tiers?.developer?.sellingPoints,
              label: 'Developer',
              subtitle: 'US$50'
            },
            {
              id: ServiceTier.TEAM,
              infoList: isOnCreditsPlan ? tiers?.team?.creditPlanSellingPoints : tiers?.team?.sellingPoints,
              label: 'Team',
              subtitle: 'US$225'
            },
            ...(isOnCreditsPlan && isGrowthPlanDisabled
              ? []
              : [
                  {
                    id: ServiceTier.GROWTH,
                    infoList: tiers?.growth?.sellingPoints,
                    label: 'Growth',
                    subtitle: 'US$1000'
                  }
                ]),
            {
              id: ServiceTier.ENTERPRISE,
              infoList: isOnCreditsPlan ? tiers?.enterprise?.creditPlanSellingPoints : tiers?.enterprise?.sellingPoints,
              label: 'Enterprise',
              subtitle: 'Custom'
            }
          ],
          required: true,
          title: 'Select your plan',
          type: 'radioCard'
        }
      ],
      id: 'plan_selection',
      nextCTA: 'Next',
      sideBarData: {
        title: 'Select plan'
      }
    },
    {
      component: 'PaymentOrCustom',
      datas: [
        {
          id: 'checkout',
          options: [],
          required: false,
          title: 'Select your plan',
          type: 'radioCard'
        }
      ],
      id: 'payment',
      nextCTA: 'Next',
      sideBarData: {
        title: 'Payment'
      }
    }
  ]
}

export const SELECTED_TIER_KEY = 'pricingTier'
